import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { usePrevious } from 'react-use'

import { useAuth } from 'app/providers/auth/AuthProvider'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useApiResponse } from 'common/hooks/useApiResponse'
import type { ICartOptionContent } from 'common/interfaces/ICartContent'
import type { ApiResponse } from 'common/interfaces/IRequestResponse'

import { useLazyFetchLevelsQuery } from 'common/store/api/rootApi'
import { SET_NEW_LEASE_ROOMS } from 'features/Booking'
import type { BOOKING_TYPE } from 'features/Booking/constants/booking.constants'
import { BOOKING_TYPE_CODE } from 'features/Booking/constants/booking.constants'
import { makeLeaseRoomsGreatAgain } from 'features/Booking/services/leaseService'
import {
  UPDATE_CART_CONTENT,
  UPDATE_CART_CONTENT_DATA,
  useCreateCartMutation,
  useDeleteCartMutation,
  useLazyFetchCartQuery,
  useUpdateCartMutation,
} from 'features/Cart'
import type { ICartData, ICartOptionData } from 'features/Cart/interfaces/ICart'
import {
  useCreateSingleCartMutation,
  useDeleteSingleCartMutation,
  useLazyFetchCartsQuery,
  useUpdateSingleCartMutation,
} from 'features/Cart/state/api/cartApi'
import {
  TOGGLE_IS_OPEN_CART_DETAILS,
  UPDATE_CART_CONNECTION,
  updateActiveBooking,
  updateCartState,
  updateLeaseConnection,
  updateSingleCart,
} from 'features/Cart/state/slices/cartSlice'
import { UPDATE_INFO_ROOMS } from 'features/Home/Book/state/slice/bookSlice'
import { SET_OPTION_TYPE_SLOTS } from 'features/Option'
import {
  SET_OPTION_TYPE_SELECT_FIXED,
  UPDATE_OPTION_TYPE_FIXED,
} from 'features/Option/state/slices/optionSlice'

export const useCartManager = () => {
  const dispatch = useAppDispatch()

  const { processApiResponse } = useApiResponse()
  const { siteId } = useParams()

  const [deleteCart] = useDeleteCartMutation({
    fixedCacheKey: 'deleting-cart',
  })

  const { cart, activeBooking } = useAppSelector((state) => state.cartReducer)
  const { optionShopItems, optionTypeSlots, optionTypeFixed, optionSelectFixed } = useAppSelector(
    (state) => state.optionReducer,
  )
  const {
    [BOOKING_TYPE_CODE.BOOKING]: bookingCart,
    [BOOKING_TYPE_CODE.STORAGE]: storageCart,
    [BOOKING_TYPE_CODE.MAILBOX]: mailboxCart,
    ...recurringCarts
  } = cart ?? {}

  const [deleteSingleCart] = useDeleteSingleCartMutation({
    fixedCacheKey: 'deleting-single-cart',
  })
  const [createSingleCart] = useCreateSingleCartMutation()
  const [editSingleCart] = useUpdateSingleCartMutation()

  const [updateCart] = useUpdateCartMutation({
    fixedCacheKey: 'updating-cart',
  })

  const [createCart] = useCreateCartMutation({
    fixedCacheKey: 'creating-cart',
  })
  const [fetchCart] = useLazyFetchCartQuery({
    refetchOnReconnect: true,
    refetchOnFocus: true,
  })

  const [fetchCarts] = useLazyFetchCartsQuery({
    refetchOnReconnect: true,
    refetchOnFocus: true,
  })

  const [fetchLevels] = useLazyFetchLevelsQuery()

  const auth = useAuth()

  const { user } = auth ?? {}

  const { newLeaseRooms } = useAppSelector((state) => state.leaseReducer)

  const prevNewLeaseRooms = usePrevious(newLeaseRooms)
  const navigate = useNavigate()

  const updateAppStore = (userCart: ICartOptionContent): void => {
    const {
      rooms = [],
      optionFixed = [],
      optionSlots = [],
      optionSelectFixed = [],
    } = userCart?.data ?? {}

    dispatch(UPDATE_CART_CONTENT(userCart))
    dispatch(SET_NEW_LEASE_ROOMS(rooms))
    dispatch(UPDATE_OPTION_TYPE_FIXED(optionFixed))
    dispatch(SET_OPTION_TYPE_SLOTS(optionSlots))
    dispatch(SET_OPTION_TYPE_SELECT_FIXED(optionSelectFixed))
    dispatch(UPDATE_INFO_ROOMS(rooms))
  }

  const fetchCartsData = async (): Promise<void> => {
    if (!user) return
    const response = await fetchCarts(siteId)
    processApiResponse(response as ApiResponse, {
      successCallback: () => {
        const responseData = response.data
        updateAppStore(responseData[BOOKING_TYPE_CODE.BOOKING])
        fetchLevels()
        dispatch(updateCartState({ key: 'cart', value: responseData }))
      },
    })
  }

  function updateActiveLeaseConnection(siteId: string | null) {
    dispatch(updateLeaseConnection(siteId))
  }

  function setActiveBooking(data: {
    bookingType: BOOKING_TYPE
    requestId?: number
    visible: boolean
  }) {
    dispatch(updateActiveBooking(data))
  }

  const fetchCartData = async (): Promise<void> => {
    if (!user) return
    const response = await fetchCart(siteId)
    processApiResponse(response as ApiResponse, {
      successCallback: () => {
        updateAppStore(response.data as ICartOptionContent)
      },
    })
  }

  const updateLocaleState = (data: ICartOptionData | null): void => {
    dispatch(SET_NEW_LEASE_ROOMS(data?.rooms || []))
    dispatch(UPDATE_CART_CONTENT_DATA(data))
  }

  const handleDeleteCart = async (): Promise<void> => {
    const response = await deleteCart(siteId)
    processApiResponse(response as ApiResponse, {
      displayError: false,
      successCallback: (): void => {
        updateAppStore(null)
      },
    })
  }

  const cartAmount = useMemo(() => {
    return (
      (mailboxCart?.data?.amount?.subtotal || 0) +
      (storageCart?.data?.amount?.subtotal || 0) +
      (bookingCart?.data?.amount?.subtotal || 0) +
      Object.values(optionShopItems || {}).reduce(
        (acc, curr) => acc + (curr?.amount?.subtotal || 0),
        0,
      ) +
      Object.values<ICartOptionContent>(recurringCarts || {}).reduce<number>(
        (acc, { data }) => acc + (data?.amount?.subtotal || 0),
        0,
      )
    )
  }, [bookingCart, mailboxCart, storageCart, recurringCarts, optionShopItems])

  const handleDeleteSingleCart = async (requestId: number): Promise<void> => {
    const response = await deleteSingleCart({ request_url_id: requestId, siteId })
    processApiResponse(response as ApiResponse, {
      successCallback: (): void => {
        dispatch(updateSingleCart({ key: requestId, data: null }))
        navigate('/')
      },
    })
  }

  async function handleCreateSingleCart(
    data: Partial<ICartOptionData>,
    siteId: string,
    request_url_id: number = activeBooking.requestId,
    callback?: (rs?: ICartOptionContent) => void,
  ) {
    const response = await createSingleCart({
      data,
      siteId,
      request_url_id,
    })

    processApiResponse(response as ApiResponse, {
      successCallback: () => {
        dispatch(updateSingleCart({ key: request_url_id, data: response.data }))
        if (callback) callback(response.data)
      },
    })
  }

  async function handleUpdateSingleCart(
    data: Partial<ICartOptionData>,
    request_url_id: number = activeBooking?.requestId,
    callback?: (rs?: ICartOptionContent) => void,
  ) {
    const response = await editSingleCart({
      data,
      request_url_id,
      siteId,
    })

    processApiResponse(response as ApiResponse, {
      successCallback: () => {
        dispatch(updateSingleCart({ key: request_url_id, data: response.data }))
        if (callback) callback(response.data)
      },
    })
  }

  function toggleMainCart() {
    dispatch(TOGGLE_IS_OPEN_CART_DETAILS())
  }

  const updateCartContent = async (cartData: ICartData): Promise<void> => {
    const rooms =
      cartData.rooms ?? (bookingCart?.data?.rooms?.length ? bookingCart.data.rooms : newLeaseRooms)

    const payload: ICartData = {
      rooms,
      optionSlots: cartData.optionSlots ?? optionTypeSlots,
      optionFixed: cartData.optionFixed ?? optionTypeFixed,
      optionSelectFixed: cartData.optionSelectFixed ?? optionSelectFixed,
      signature: cartData.signature ?? bookingCart?.data?.signature,
      staff: cartData.staff ?? bookingCart?.data?.staff,
    }

    const isEmptyCart: boolean = !bookingCart?.data
    const isEmptyRoomsData: boolean = !payload.rooms?.length
    const hasSlots = payload.rooms
      ?.flatMap((room) => room.selectedDays ?? [])
      .some((day) => (day?.selectedSlots?.length ?? 0) > 0)

    const isValidCart: boolean = !!Object.values(payload).flat()?.length
    const isDeleteRequest: boolean = !isValidCart || isEmptyRoomsData || !hasSlots
    const isCreateRequest: boolean = isEmptyCart && !!payload.rooms?.length
    const isUpdateRequest: boolean = !isEmptyCart && isValidCart && !isEmptyRoomsData && hasSlots

    switch (true) {
      case isUpdateRequest: {
        const updated = {
          ...payload,
          rooms: makeLeaseRoomsGreatAgain(payload.rooms),
        }

        const responseUpdate = await updateCart({ data: updated, siteId })
        processApiResponse(responseUpdate as ApiResponse, {
          error: 'Update cart error',
          errorCallback: () => dispatch(SET_NEW_LEASE_ROOMS(prevNewLeaseRooms)),
          successCallback: () => {
            const { data: responseData } = responseUpdate
            updateAppStore(responseData)
          },
        })
        return
      }
      case isCreateRequest: {
        const responseCreate = await createCart({
          siteId,
          data: { ...payload, rooms: makeLeaseRoomsGreatAgain(payload.rooms) },
        })
        updateLocaleState(responseCreate.data.data)
        dispatch(UPDATE_CART_CONNECTION(responseCreate.data.database_connection))

        processApiResponse(responseCreate as ApiResponse, {
          error: 'Create cart error',
        })
        return
      }
      case isDeleteRequest:
        await handleDeleteCart()
        return
      default:
        return
    }
  }

  return {
    updateCartContent,
    fetchCartsData,
    fetchCartData,
    deleteCart: handleDeleteCart,
    handleDeleteSingleCart,
    handleCreateSingleCart,
    handleUpdateSingleCart,
    toggleMainCart,
    setActiveBooking,
    updateActiveLeaseConnection,
    updateAppStore,
    bookingCart,
    storageCart,
    mailboxCart,
    recurringCarts,
    cart,
    activeBooking,
    cartAmount,
  }
}
